import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Button,
  Box,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  tacticalActionSx,
} from "../../utils/fdcTheme";
import { useAuth } from "../../contexts/AuthContext";
import UserManagementPanel from "./UserManagementPanel";
import WeaponsPanel from "./WeaponsPanel";
import TakPanel from "./TakPanel";
import MapsPanel from "./MapsPanel";

// A collapsible sub-section inside the Admin accordion.
function AdminSection({ title, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&::before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.5 } }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function AdminPanel({
  expanded,
  onChange,
  handleClearDatabase,
  exportDatabase,
}) {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme, "danger")}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Admin
          </Typography>
          <Chip
            size="small"
            label="Danger zone"
            sx={(theme) => tacticalChipSx(theme, "danger")}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5 }, pb: 1.25, pt: 1 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => exportDatabase("csv")}
              sx={(theme) => ({ ...tacticalActionSx(theme), minHeight: 40 })}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => exportDatabase("xlsx")}
              sx={(theme) => ({ ...tacticalActionSx(theme), minHeight: 40 })}
            >
              Export Excel
            </Button>
            <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteForeverRoundedIcon />}
              onClick={handleClearDatabase}
              sx={(theme) => ({ ...tacticalActionSx(theme, "danger"), minHeight: 40 })}
            >
              Clear database
            </Button>
          </Stack>

          {isAdmin && (
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              <AdminSection title="User management"><UserManagementPanel /></AdminSection>
              <AdminSection title="Weapons & firing tables"><WeaponsPanel /></AdminSection>
              <AdminSection title="TAK server link"><TakPanel /></AdminSection>
              <AdminSection title="Offline maps"><MapsPanel /></AdminSection>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
